import type { ContentItem } from '@kidshub/contracts';
import { type FormEvent, type KeyboardEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/kit.js';
import { SuggestionChip } from './suggestion-chip.js';
import './rename-field.css';

/** `contentItemSchema.title`'s ceiling. The tablet clamps long names to two lines. */
export const TITLE_MAX_LENGTH = 80;

interface RenameFieldProps {
  item: ContentItem;
  /** A tidier name to offer, or null. See `title-suggestion.ts`. */
  suggestion: string | null;
  onSave: (title: string) => void;
  onCancel?: () => void;
  isBusy?: boolean;
  autoFocus?: boolean;
}

/**
 * Naming one item, wherever that happens: inline in a collection's list and in
 * a library card's settings. One component, so that a name is edited, suggested
 * and restored the same way in both places.
 */
export const RenameField = ({
  item,
  suggestion,
  onSave,
  onCancel,
  isBusy = false,
  autoFocus = false,
}: RenameFieldProps) => {
  const { t } = useTranslation();
  const inputId = useId();
  const [value, setValue] = useState(item.title);

  const trimmed = value.trim();
  const original = item.originalTitle;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (trimmed === '' || trimmed === item.title) {
      onCancel?.();
      return;
    }
    onSave(trimmed);
  };

  const cancelOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && onCancel !== undefined) {
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <form className="cp-rename" onSubmit={submit} onKeyDown={cancelOnEscape}>
      <label className="sr" htmlFor={inputId}>
        {t('collectionPage.name')}
      </label>
      <input
        id={inputId}
        className="input"
        value={value}
        maxLength={TITLE_MAX_LENGTH}
        autoComplete="off"
        // biome-ignore lint/a11y/noAutofocus: opened by the parent's own tap on the name, to type into
        autoFocus={autoFocus}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setValue(event.target.value)}
      />
      <div className="cp-rename-foot">
        <span className="cp-count num">
          {t('collectionPage.count', { count: trimmed.length, max: TITLE_MAX_LENGTH })}
        </span>
        {suggestion === null || suggestion === trimmed ? null : (
          <SuggestionChip onClick={() => setValue(suggestion)}>{suggestion}</SuggestionChip>
        )}
        {original === null || original === trimmed ? null : (
          <Button variant="ghost" small onClick={() => setValue(original)}>
            {t('collectionPage.useOriginal')}
          </Button>
        )}
        <span className="cp-rename-actions">
          {onCancel === undefined ? null : (
            <Button variant="ghost" small onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
          <Button variant="primary" small type="submit" disabled={isBusy || trimmed === ''}>
            {t('common.save')}
          </Button>
        </span>
      </div>
    </form>
  );
};
